import { BookingInfoComponent } from '@components/shared/booking-info/booking-info.component';
import { CountryInfoComponent } from '@components/shared/country-info/country-info.component';
import { EmptyStateComponent } from '@components/shared/empty-state/empty-state.component';
import { PaginationComponent } from '@components/shared/pagination/pagination.component';
import { BookingInfo } from '@models/booking-info.model';
import { PaginationEvent } from '@models/pagination.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BookingInfoService } from '@services/util/booking-info.service';
import { LogService } from '@services/util/log.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputSwitchModule } from 'primeng/inputswitch';
import { PaginatorModule } from 'primeng/paginator';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { finalize, Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { HostActivityLogService } from '../../../shared/host-activity-log.service';
import { checkinDeleteMetadata } from '../../../shared/host-activity.metadata';
import { CheckinFilter } from '../../models/checkin-filter.model';
import { CheckinRow, PaginatedCheckins } from '../../models/checkin.model';
import { CheckinService } from '../../services/checkin.service';

/**
 * Interface for table sort events
 */
interface SortEvent {
  field: string;
  order: number;
  data?: CheckinRow[];
}

@Component({
  selector: 'app-checkins-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    BadgeModule,
    PaginatorModule,
    ToastModule,
    TooltipModule,
    ConfirmDialogModule,
    InputSwitchModule,
    PaginationComponent,
    EmptyStateComponent,
    BookingInfoComponent,
    CountryInfoComponent
  ],
  providers: [ConfirmationService, MessageService],
  template: `
    <div>
      <!-- Toast for success/error messages -->
      <p-toast></p-toast>

      <!-- Confirmation Dialog for Delete Operations -->
      <p-confirmDialog></p-confirmDialog>

      <!-- Loading Indicator -->
      @if (loading) {
        <div class="my-6 flex flex-col items-center justify-center">
          <!-- Tailwind Spinner -->
          <div class="relative h-14 w-14">
            <div class="absolute h-14 w-14 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
          </div>
          <span class="mt-3 font-medium">{{ 'common.loading' | translate }}</span>
        </div>
      }

      <!-- Table with checkins -->
      @if (!loading && paginatedCheckins && paginatedCheckins.items && paginatedCheckins.items.length > 0) {
        <div class="">
          <p-table
            #dt
            [value]="paginatedCheckins.items || []"
            [stripedRows]="true"
            styleClass="p-datatable-sm"
            [rowHover]="true"
            [tableStyle]="{ 'min-width': '60rem' }"
            responsiveLayout="scroll"
            (sortFunction)="onSort($event)"
            [customSort]="true"
            dataKey="id"
          >
            <ng-template pTemplate="header">
              <tr>
                <th style="vertical-align: middle">
                  {{ 'checkins.table.booking' | translate }}
                </th>
                <th
                  [pSortableColumn]="'name'"
                  style="vertical-align: middle"
                >
                  <div class="flex justify-between">
                    {{ 'checkins.table.name' | translate }}
                    <span class="ml-1">
                      @if (sortState.field === 'name' && sortState.direction === 'asc') {
                        <i class="pi pi-sort-up"></i>
                      }
                      @if (sortState.field === 'name' && sortState.direction === 'desc') {
                        <i class="pi pi-sort-down"></i>
                      }
                      @if (sortState.field !== 'name') {
                        <i class="pi pi-sort text-300"></i>
                      }
                    </span>
                  </div>
                </th>
                <th style="vertical-align: middle">
                  {{ 'checkins.table.email' | translate }}
                </th>
                <th style="vertical-align: middle">
                  {{ 'checkins.table.country' | translate }}
                </th>
                <th style="vertical-align: middle">
                  {{ 'checkins.table.city' | translate }}
                </th>
                <th style="vertical-align: middle">
                  {{ 'checkins.table.newsletterSignup' | translate }}
                </th>
                <th style="vertical-align: middle">
                  {{ 'checkins.table.actions' | translate }}
                </th>
              </tr>
            </ng-template>

            <ng-template
              pTemplate="body"
              let-checkin
            >
              <tr (click)="onRowClick(checkin.id)" class="!cursor-pointer">
                <td>
                  @if (checkin.bookings && checkin.bookings.length > 0) {
                    <div class="flex items-center gap-2">
                      <app-booking-info
                        [bookingInfo]="createBookingInfo(checkin)"
                        (click)="$event.stopPropagation()"
                      ></app-booking-info>
                      @if (checkin.bookings.length > 1) {
                        <p-badge
                          [value]="'+' + (checkin.bookings.length - 1)"
                          severity="info"
                          styleClass="text-xs"
                        ></p-badge>
                      }
                    </div>
                  }
                </td>
                <td>{{ checkin.name }}</td>
                <td>{{ checkin.email }}</td>
                <td>
                  <app-country-info
                    [countryCode]="checkin.countryCode"
                    [showName]="true"
                    [showIsoCode]="false"
                    [showIsoCodeOnMobile]="false"
                    [showNameOnMobile]="false"
                  ></app-country-info>
                </td>
                <td>{{ checkin.city }}</td>
                <td>
                  <p-inputSwitch
                    [ngModel]="!!checkin.newsletterSignup"
                    (onChange)="toggleNewsletter(checkin, $event)"
                  ></p-inputSwitch>
                </td>
                <td>
                  <div class="flex  gap-2">
                    <button
                      pButton
                      icon="pi pi-trash"
                      class="p-button-rounded p-button-text p-button p-button-danger"
                      (click)="confirmDelete(checkin, $event)"
                      title="{{ 'common.delete' | translate }}"
                      pTooltip="{{ 'common.delete' | translate }}"
                      tooltipPosition="top"
                    ></button>
                  </div>
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td [attr.colspan]="7">
                  <app-empty-state
                    [title]="'checkins.noCheckins'"
                    [description]="'checkins.noCheckinsDescription'"
                  >
                  </app-empty-state>
                </td>
              </tr>
            </ng-template>
          </p-table>

          <!-- Pagination controls -->
          <app-pagination
            [pageSize]="pageSize"
            [currentPage]="currentPage"
            [totalItems]="paginatedCheckins.totalCount"
            (pageChange)="onPaginationChange($event)"
          >
          </app-pagination>
        </div>
      } @else if (!loading) {
        <app-empty-state
          [title]="'checkins.noCheckins'"
          [description]="'checkins.noCheckinsDescription'"
        >
        </app-empty-state>
      }
    </div>
  `
})
export class CheckinsTableComponent implements OnInit, OnDestroy {
  // Default page size - matches API default of 30
  readonly pageSize = 30;

  // Current page (1-based)
  currentPage = 1;

  // Checkins data with pagination details
  paginatedCheckins: PaginatedCheckins | null = null;

  // Loading state
  loading = false;

  // Current sort field and order
  sortField: string | null = 'name';
  sortOrder: 'asc' | 'desc' | null = 'asc';

  // Keeps track of which column is sorted and direction
  sortState: { field: string | null; direction: 'asc' | 'desc' | null } = {
    field: 'name',
    direction: 'asc'
  };

  // Active filters object
  activeFilter?: CheckinFilter;

  // Input for external filter changes
  @Input() set filter(filter: CheckinFilter | undefined) {
    if (filter) {
      this.activeFilter = filter;
      // When filter changes, reset to first page and reload data
      this.currentPage = 1;
      this.loadCheckins();
    }
  }

  // Output events
  @Output() viewCheckinDetails = new EventEmitter<string>();

  // Component cleanup
  private readonly destroy$ = new Subject<void>();
  private readonly hostActivityLog = inject(HostActivityLogService);

  constructor(
    private readonly checkinService: CheckinService,
    private readonly log: LogService,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
    private readonly router: Router,
    private readonly bookingInfoService: BookingInfoService
  ) {}

  ngOnInit(): void {
    // Load initial data
    this.loadCheckins();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle filter changes from the filter component
   */
  onFilterChange(filter: CheckinFilter): void {
    // Save active filter
    this.activeFilter = Object.keys(filter).length > 0 ? filter : undefined;
    this.log.debug('Applied filters:', this.activeFilter);

    // Reset to first page when applying filters
    this.currentPage = 1;

    // Load with filters
    this.loadCheckins();
  }

  /**
   * Load checkins for the current page with optional sort and filters
   */
  loadCheckins(): void {
    this.loading = true;

    this.checkinService
      .getCheckins(this.currentPage, this.pageSize, this.sortField, this.sortOrder, this.activeFilter)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (data) => {
          this.paginatedCheckins = data;
          this.log.debug('Checkins loaded:', data);
        },
        error: (error) => {
          this.log.error('Error loading checkins:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('error.server.title'),
            detail: this.translateService.instant('checkins.loadError'),
            life: 5000
          });
        }
      });
  }

  /**
   * Handle pagination event from shared pagination component
   */
  onPaginationChange(event: PaginationEvent): void {
    this.currentPage = event.page;
    this.loadCheckins();
  }

  /**
   * Custom sort handler for p-table
   */
  onSort(event: SortEvent): CheckinRow[] {
    const clickedField = event.field;

    // If clicking on same column, toggle sort direction: none -> asc -> desc -> none
    if (clickedField === this.sortState.field) {
      // Toggle direction: null -> asc -> desc -> null
      if (this.sortState.direction === null) {
        this.sortState.direction = 'asc';
      } else if (this.sortState.direction === 'asc') {
        this.sortState.direction = 'desc';
      } else {
        // Reset sort completely when going beyond desc
        this.sortState.field = null;
        this.sortState.direction = null;
      }
    } else {
      // Clicking new column, start with asc
      this.sortState.field = clickedField;
      this.sortState.direction = 'asc';
    }

    // Save sort state for API requests
    this.sortField = this.sortState.field;
    this.sortOrder = this.sortState.direction;

    // Reload data with new sort
    this.loadCheckins();

    return event.data || [];
  }

  /**
   * Navigate to checkin details page
   * @param id Checkin ID
   */
  onRowClick(id: number): void {
    this.log.debug('Navigating to checkin details page for ID:', id);
    this.router.navigate(['/checkins', id]);
    this.viewCheckinDetails.emit(id.toString());
  }

  /**
   * Create BookingInfo object from checkin first booking data
   * @param checkin Checkin row data
   * @returns BookingInfo object (from first booking in array)
   */
  createBookingInfo(checkin: CheckinRow): BookingInfo {
    if (!checkin.bookings || checkin.bookings.length === 0) {
      return new BookingInfo(0, '');
    }

    const firstBooking = checkin.bookings[0];
    return new BookingInfo(
      firstBooking.id || 0,
      firstBooking.name || firstBooking.reference || '',
      firstBooking.startDate ? new Date(firstBooking.startDate) : undefined,
      firstBooking.endDate ? new Date(firstBooking.endDate) : undefined
    );
  }

  /**
   * Delete checkin with confirmation
   */
  confirmDelete(checkin: CheckinRow, event: Event): void {
    event.stopPropagation();
    const id = checkin.id;
    const flats = checkin.bookings.flatMap((booking) => booking.flats);

    this.confirmationService.confirm({
      message: this.translateService.instant('checkins.confirmDelete'),
      header: this.translateService.instant('common.delete.confirmHeader'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('common.yesDelete'),
      rejectLabel: this.translateService.instant('common.cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.loading = true;

        this.checkinService
          .deleteCheckin(id)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.loading = false))
          )
          .subscribe({
            next: (success: boolean) => {
              if (success) {
                this.hostActivityLog.log('checkin_delete', checkinDeleteMetadata({ checkinId: id, flats }));
                this.messageService.add({
                  severity: 'success',
                  summary: this.translateService.instant('common.success'),
                  detail: this.translateService.instant('checkins.deleteSuccess')
                });

                // Reload data after successful delete
                this.loadCheckins();
              } else {
                this.messageService.add({
                  severity: 'error',
                  summary: this.translateService.instant('common.error'),
                  detail: this.translateService.instant('checkins.deleteError')
                });
              }
            },
            error: (error: unknown) => {
              this.log.error('Error deleting checkin:', error);
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('common.error'),
                detail: this.translateService.instant('checkins.deleteError')
              });
            }
          });
      }
    });
  }

  /**
   * Toggle newsletter subscription status
   * @param checkin Checkin to toggle newsletter status for
   * @param event Input switch change event
   */
  toggleNewsletter(checkin: CheckinRow, event: any): void {
    // Stop event propagation to prevent row click
    event.originalEvent?.stopPropagation();

    // Get the new status from the switch checked property
    const newStatus = event.checked;

    // If the new status is the same as the current status, do nothing
    if (!!checkin.newsletterSignup === newStatus) {
      return;
    }

    const messageKey = newStatus ? 'checkins.subscribeNewsletterConfirm' : 'checkins.unsubscribeNewsletterConfirm';

    this.confirmationService.confirm({
      message: this.translateService.instant(messageKey),
      header: this.translateService.instant('common.confirm'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.loading = true;

        this.checkinService
          .updateCheckinNewsletter(checkin.id, newStatus)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.loading = false))
          )
          .subscribe({
            next: () => {
              // Update local model
              checkin.newsletterSignup = newStatus;

              const successKey = newStatus ? 'checkins.subscribeSuccess' : 'checkins.unsubscribeSuccess';
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant('common.success'),
                detail: this.translateService.instant(successKey)
              });
            },
            error: (error: unknown) => {
              // Revert local model on error
              checkin.newsletterSignup = !newStatus;

              const errorKey = newStatus ? 'checkins.subscribeError' : 'checkins.unsubscribeError';
              this.log.error('Error updating newsletter status:', error);
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('common.error'),
                detail: this.translateService.instant(errorKey)
              });
            }
          });
      },
      reject: () => {
        // Reset the switch to its original state if user cancels
        setTimeout(() => {
          checkin.newsletterSignup = !newStatus;
          // Force change detection - create new reference with non-nullable properties
          if (this.paginatedCheckins) {
            const items = [...(this.paginatedCheckins.items || [])];
            this.paginatedCheckins = {
              items: items,
              totalCount: this.paginatedCheckins.totalCount || 0,
              page: this.paginatedCheckins.page || 1,
              itemsPerPage: this.paginatedCheckins.itemsPerPage || this.pageSize
            };
          }
        });
      }
    });
  }
}
